<template>
  <div class="coulomb-viz-3d">
    
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$F = k \cdot \frac{q_1 q_2}{r^2}$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 3D 渲染 -->
      <div ref="canvasContainer" class="simulation-stage dark-stage canvas-3d-wrapper">
         <div class="hud-center" v-if="forceValue > 0">
            <div class="val">{{ forceValue.toFixed(1) }} <small>N</small></div>
         </div>
      </div>

      <!-- 参数控制 -->
      <div class="compact-controls">
         <div class="control-card glass">
            <div class="control-vertical-group">
               <div class="param-row">
                  <div class="s-item">
                     <label>电荷 1 (q₁): {{ q1 }} μC</label>
                     <el-slider v-model="q1" :min="-10" :max="10" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item">
                     <label>电荷 2 (q₂): {{ q2 }} μC</label>
                     <el-slider v-model="q2" :min="-10" :max="10" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item" style="grid-column: span 2; margin-top: 10px;">
                     <label>间距 (r): {{ distance }} m</label>
                     <el-slider v-model="distance" :min="10" :max="60" input-size="mini"></el-slider>
                  </div>
               </div>
            </div>
         </div>

         <div class="discovery-card-compact" v-latex>
            <i>🔍</i>
            <p><strong>互动观察：</strong>同性电荷相斥，异性电荷相吸。距离缩小一半，静电力将增大为原来的 <strong>4 倍</strong>。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'CoulombLawViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      q1: 5,
      q2: -5,
      distance: 30,
      running: true
    }
  },
  computed: {
    forceValue() {
      // F = k * |q1*q2| / r^2 (simplified scale)
      return (Math.abs(this.q1 * this.q2) * 1000) / (this.distance * this.distance);
    },
    isAttractive() {
      return (this.q1 * this.q2) < 0;
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initThree();
    });
  },
  beforeDestroy() {
    this.running = false;
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    if (this.renderer) {
      this.renderer.dispose();
      this.$refs.canvasContainer.removeChild(this.renderer.domElement);
    }
  },
  watch: {
    q1() { this.updateSymmetry(); },
    q2() { this.updateSymmetry(); },
    distance() { this.updateSymmetry(); }
  },
  methods: {
    initThree() {
      this.canvasContainer = this.$refs.canvasContainer;
      const width = this.canvasContainer.clientWidth || 400;
      const height = this.canvasContainer.clientHeight || 200;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
      this.camera.position.set(0, 40, 100);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.canvasContainer.appendChild(this.renderer.domElement);

      // Charges
      this.charge1 = new THREE.Mesh(new THREE.SphereGeometry(6, 32, 32), new THREE.MeshPhongMaterial({ color: 0xff4757 }));
      this.charge2 = new THREE.Mesh(new THREE.SphereGeometry(6, 32, 32), new THREE.MeshPhongMaterial({ color: 0x00d2ff }));
      this.scene.add(this.charge1);
      this.scene.add(this.charge2);

      // Force Arrows
      this.arrowL = new THREE.ArrowHelper(new THREE.Vector3(1,0,0), new THREE.Vector3(0,0,0), 10, 0xffffff);
      this.arrowR = new THREE.ArrowHelper(new THREE.Vector3(-1,0,0), new THREE.Vector3(0,0,0), 10, 0xffffff);
      this.scene.add(this.arrowL);
      this.scene.add(this.arrowR);

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.5));
      const light = new THREE.PointLight(0xffffff, 1, 500);
      light.position.set(0, 50, 50);
      this.scene.add(light);

      this.updateSymmetry();
      this.animate();
    },
    updateSymmetry() {
      const halfDist = this.distance / 2;
      this.charge1.position.x = -halfDist;
      this.charge2.position.x = halfDist;
      
      this.charge1.material.color.set(this.q1 >= 0 ? 0xff4757 : 0x00d2ff);
      this.charge2.material.color.set(this.q2 >= 0 ? 0xff4757 : 0x00d2ff);

      const f = this.forceValue / 5; // arrow scale
      const dir = this.isAttractive ? 1 : -1;
      
      this.arrowL.setDirection(new THREE.Vector3(dir, 0, 0));
      this.arrowL.position.set(-halfDist, 0, 0);
      this.arrowL.setLength(Math.max(2, f), 4, 2);

      this.arrowR.setDirection(new THREE.Vector3(-dir, 0, 0));
      this.arrowR.position.set(halfDist, 0, 0);
      this.arrowR.setLength(Math.max(2, f), 4, 2);
    },
    animate() {
      if (!this.running) return;
      this.animFrame = requestAnimationFrame(this.animate);
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    }
  }
}
</script>

<style scoped lang="scss">
.coulomb-viz-3d { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #10b981; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.main-layout { display: flex; gap: 12px; height: 100%; min-height: 0; flex-direction: column; }
.simulation-stage { flex: 1; min-height: 200px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; display: flex; justify-content: center; align-items: center; 
  .hud-center {
    position: absolute; top: 15px; width: 100%; text-align: center;
    .val { font-size: 1.2rem; color: #fff; font-weight: bold; font-family: monospace; text-shadow: 0 0 10px rgba(255,255,255,0.5); }
  }
}
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .param-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: -5px; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { font-style: normal; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
